import React, { useState } from 'react';
import { Star, X, Briefcase, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ReviewModal({ contractor, onClose, onSubmitted, currentUserId }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { toast({ title: 'Please select a rating', variant: 'destructive' }); return; }
    if (title.length < 3) { toast({ title: 'Title must be at least 3 characters', variant: 'destructive' }); return; }
    if (content.length < 10) { toast({ title: 'Review must be at least 10 characters', variant: 'destructive' }); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewee_id: contractor.contractor_id,
          rating,
          title,
          content
        })
      });
      if (res.ok) {
        toast({ title: 'Review submitted!', description: `Your review for ${contractor.name} has been posted.` });
        onSubmitted();
        onClose();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed to submit review', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error submitting review', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="review-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#3B82F6]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-[#3B82F6] border-b-2 border-black">
          <div className="flex items-center gap-2">
            <Star size={22} weight="bold" className="text-white" />
            <h3 className="text-white font-black uppercase text-sm tracking-wider">Review Contractor</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="review-close-btn">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Contractor Info */}
        <div className="p-4 border-b-2 border-neutral-200 dark:border-neutral-700 flex items-center gap-3">
          <div className="w-12 h-12 bg-[#2563EB] border-2 border-black flex items-center justify-center overflow-hidden flex-shrink-0">
            {contractor.profile_image ? (
              <img src={contractor.profile_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <Briefcase size={22} weight="bold" className="text-white" />
            )}
          </div>
          <div>
            <p className="font-bold text-neutral-900 dark:text-white">{contractor.name}</p>
            {contractor.company && <p className="text-xs text-neutral-500">{contractor.company}</p>}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Star Rating */}
          <div>
            <p className="text-xs font-bold uppercase text-neutral-500 mb-2">Your Rating</p>
            <div className="flex gap-1" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHoverRating(s)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${s}`}
                >
                  <Star
                    size={32}
                    weight={(hoverRating || rating) >= s ? 'fill' : 'regular'}
                    className={(hoverRating || rating) >= s ? 'text-amber-400' : 'text-neutral-300 dark:text-neutral-600'}
                  />
                </button>
              ))}
              {rating > 0 && <span className="ml-2 text-sm font-bold text-neutral-600 dark:text-neutral-400 self-center">{rating}/5</span>}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-bold uppercase text-neutral-500 block mb-1">Review Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience..."
              maxLength={100}
              className="w-full h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6]"
              data-testid="review-title-input"
            />
          </div>

          {/* Content */}
          <div>
            <label className="text-xs font-bold uppercase text-neutral-500 block mb-1">Your Review</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share details about your experience with this contractor..."
              maxLength={1000}
              rows={4}
              className="w-full px-3 py-2 text-sm bg-neutral-100 dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6] resize-none"
              data-testid="review-content-input"
            />
            <p className="text-xs text-neutral-400 mt-1">{content.length}/1000</p>
          </div>

          <button
            type="submit"
            disabled={submitting || rating === 0}
            className="w-full h-12 bg-[#3B82F6] hover:bg-[#E55D00] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="review-submit-btn"
          >
            {submitting ? (
              <><ArrowsClockwise size={20} weight="bold" className="animate-spin" /> Submitting...</>
            ) : (
              <><Star size={20} weight="bold" /> Submit Review</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

